import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Euro, Users, Wrench, Ticket, FileText, 
  Calendar, Download, RefreshCw, ArrowUpRight, ArrowDownRight, Target
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { IncassiReport } from '../components/reports/IncassiReport';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#22d3ee', '#a855f7', '#f59e0b', '#10b981', '#ef4444', '#3b82f6'];

export default function ReportsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('trimestre');
  const [stats, setStats] = useState({
    fatturato: { totale: 0, trend: 0, perMese: [] },
    interventi: { totale: 0, trend: 0, perTecnico: [], perMese: [] },
    tickets: { totale: 0, aperti: 0, chiusi: 0, perPriorita: [] },
    clienti: { totale: 0, nuovi: 0, topClienti: [] }
  });

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch tutti i dati necessari
      const [interventiRes, ticketsRes, clientiRes, documentiRes] = await Promise.all([
        axios.get(`${API}/interventi`),
        axios.get(`${API}/tickets`),
        axios.get(`${API}/clienti`),
        axios.get(`${API}/documenti`)
      ]);

      const interventi = interventiRes.data;
      const tickets = ticketsRes.data;
      const clienti = clientiRes.data;
      const documenti = documentiRes.data;

      // Calcola range date in base al periodo
      const now = new Date();
      let startDate;
      switch (periodo) {
        case 'mese':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'trimestre':
          startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
          break;
        case 'semestre':
          startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
          break;
        case 'anno':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        default:
          startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      }

      // Filtra dati per periodo
      const interventiPeriodo = interventi.filter(i => 
        new Date(i.data_intervento || i.created_at) >= startDate
      );
      const ticketsPeriodo = tickets.filter(t => new Date(t.created_at) >= startDate);
      const documentiPeriodo = documenti.filter(d => new Date(d.created_at) >= startDate);

      // Calcola fatturato
      const fatturatoDocs = documentiPeriodo
        .filter(d => d.tipo === 'fattura')
        .reduce((sum, d) => sum + (d.importo_totale || 0), 0);
      
      const fatturatoInterventi = interventiPeriodo
        .reduce((sum, i) => sum + (i.costo_totale || 0), 0);
      
      const fatturato = fatturatoDocs + fatturatoInterventi;

      // Fatturato per mese
      const fatturatoPerMese = {};
      documentiPeriodo.filter(d => d.tipo === 'fattura').forEach(d => {
        const month = new Date(d.created_at).toLocaleString('it-IT', { month: 'short' });
        fatturatoPerMese[month] = (fatturatoPerMese[month] || 0) + (d.importo_totale || 0);
      });
      interventiPeriodo.forEach(i => {
        const month = new Date(i.data_intervento || i.created_at).toLocaleString('it-IT', { month: 'short' });
        fatturatoPerMese[month] = (fatturatoPerMese[month] || 0) + (i.costo_totale || 0);
      });

      // Interventi per tecnico
      const interventiPerTecnico = {};
      interventiPeriodo.forEach(i => {
        const nome = i.tecnico_nome || 'Non assegnato';
        if (!interventiPerTecnico[nome]) {
          interventiPerTecnico[nome] = { nome, interventi: 0, fatturato: 0 };
        }
        interventiPerTecnico[nome].interventi++;
        interventiPerTecnico[nome].fatturato += (i.costo_totale || 0);
      });

      // Top clienti per fatturato
      const fatturatoPerCliente = {};
      documentiPeriodo.filter(d => d.tipo === 'fattura').forEach(d => {
        const cliente = clienti.find(c => c.id === d.cliente_id);
        const nome = cliente?.ragione_sociale || 'Sconosciuto';
        fatturatoPerCliente[nome] = (fatturatoPerCliente[nome] || 0) + (d.importo_totale || 0);
      });
      interventiPeriodo.forEach(i => {
        const cliente = clienti.find(c => c.id === i.cliente_id);
        const nome = cliente?.ragione_sociale || 'Sconosciuto';
        fatturatoPerCliente[nome] = (fatturatoPerCliente[nome] || 0) + (i.costo_totale || 0);
      });

      const topClienti = Object.entries(fatturatoPerCliente)
        .map(([nome, fatturato]) => ({ nome, fatturato }))
        .sort((a, b) => b.fatturato - a.fatturato)
        .slice(0, 5);

      // Tickets per priorità
      const ticketsPerPriorita = [
        { name: 'Urgente', value: ticketsPeriodo.filter(t => t.priorita === 'urgente').length, color: '#ef4444' },
        { name: 'Alta', value: ticketsPeriodo.filter(t => t.priorita === 'alta').length, color: '#f59e0b' },
        { name: 'Media', value: ticketsPeriodo.filter(t => t.priorita === 'media').length, color: '#3b82f6' },
        { name: 'Bassa', value: ticketsPeriodo.filter(t => t.priorita === 'bassa').length, color: '#10b981' },
      ];

      setStats({
        fatturato: {
          totale: fatturato,
          trend: 12.5, // Placeholder - da calcolare comparando col periodo precedente
          perMese: Object.entries(fatturatoPerMese).map(([mese, valore]) => ({ mese, valore }))
        },
        interventi: {
          totale: interventiPeriodo.length,
          trend: 8.2,
          perTecnico: Object.values(interventiPerTecnico).sort((a, b) => b.interventi - a.interventi),
          perMese: [] // Da implementare
        },
        tickets: {
          totale: ticketsPeriodo.length,
          aperti: ticketsPeriodo.filter(t => t.stato === 'aperto').length,
          chiusi: ticketsPeriodo.filter(t => t.stato === 'chiuso').length,
          perPriorita: ticketsPerPriorita
        },
        clienti: {
          totale: clienti.length,
          nuovi: clienti.filter(c => new Date(c.created_at) >= startDate).length,
          topClienti
        }
      });

    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Errore nel caricamento delle statistiche');
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const exportPDF = () => {
    toast.info('Export PDF in sviluppo...');
    // TODO: Implementare export PDF con jspdf
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Report & Statistiche</h1>
          <p className="text-muted-foreground text-sm mt-1">Analisi performance e fatturato</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={periodo} onValueChange={setPeriodo}>
            <SelectTrigger className="w-40 rounded-md">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-md">
              <SelectItem value="mese">Ultimo mese</SelectItem>
              <SelectItem value="trimestre">Ultimo trimestre</SelectItem>
              <SelectItem value="semestre">Ultimo semestre</SelectItem>
              <SelectItem value="anno">Anno corrente</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="icon" onClick={fetchStats} className="rounded-md">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          
          <Button onClick={exportPDF} className="rounded-md">
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Fatturato */}
        <Card className="rounded-md border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase text-muted-foreground">Fatturato</p>
                <p className="text-2xl font-black text-emerald-600">
                  {formatCurrency(stats.fatturato.totale)}
                </p>
              </div>
              <div className={`flex items-center gap-1 text-sm ${stats.fatturato.trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {stats.fatturato.trend >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                {Math.abs(stats.fatturato.trend)}%
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Interventi */}
        <Card className="rounded-md border-l-4 border-l-cyan-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase text-muted-foreground">Interventi</p>
                <p className="text-2xl font-black text-cyan-600">{stats.interventi.totale}</p>
              </div>
              <Wrench className="h-8 w-8 text-cyan-500/30" />
            </div>
          </CardContent>
        </Card>

        {/* Tickets */}
        <Card className="rounded-md border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase text-muted-foreground">Tickets</p>
                <p className="text-2xl font-black text-purple-600">{stats.tickets.totale}</p>
                <p className="text-xs text-muted-foreground">
                  {stats.tickets.aperti} aperti / {stats.tickets.chiusi} chiusi
                </p>
              </div>
              <Ticket className="h-8 w-8 text-purple-500/30" />
            </div>
          </CardContent>
        </Card>

        {/* Clienti */}
        <Card className="rounded-md border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase text-muted-foreground">Clienti</p>
                <p className="text-2xl font-black text-amber-600">{stats.clienti.totale}</p>
                <p className="text-xs text-muted-foreground">
                  +{stats.clienti.nuovi} nuovi
                </p>
              </div>
              <Users className="h-8 w-8 text-amber-500/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Riepilogo Incassi mensile */}
      <IncassiReport />

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fatturato per mese */}
        <Card className="rounded-md">
          <CardHeader>
            <CardTitle className="text-sm font-mono uppercase flex items-center gap-2">
              <Euro className="h-4 w-4 text-emerald-500" />
              Fatturato per Mese
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={stats.fatturato.perMese}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="mese" stroke="#666" fontSize={12} />
                <YAxis stroke="#666" fontSize={12} tickFormatter={(v) => `€${v/1000}k`} />
                <Tooltip 
                  formatter={(value) => [formatCurrency(value), 'Fatturato']}
                  contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="valore" 
                  stroke="#10b981" 
                  fill="#10b981" 
                  fillOpacity={0.3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Tickets per priorità */}
        <Card className="rounded-md">
          <CardHeader>
            <CardTitle className="text-sm font-mono uppercase flex items-center gap-2">
              <Ticket className="h-4 w-4 text-purple-500" />
              Tickets per Priorità
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={stats.tickets.perPriorita}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {stats.tickets.perPriorita.map((entry) => (
                    <Cell key={`cell-${entry.name}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Performance Tecnici */}
        <Card className="rounded-md">
          <CardHeader>
            <CardTitle className="text-sm font-mono uppercase flex items-center gap-2">
              <Wrench className="h-4 w-4 text-cyan-500" />
              Performance Tecnici
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={stats.interventi.perTecnico} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis type="number" stroke="#666" fontSize={12} />
                <YAxis type="category" dataKey="nome" stroke="#666" fontSize={11} width={100} />
                <Tooltip 
                  formatter={(value, name) => [name === 'interventi' ? value : formatCurrency(value), name === 'interventi' ? 'Interventi' : 'Fatturato']}
                  contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                />
                <Bar dataKey="interventi" fill="#22d3ee" name="Interventi" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Clienti */}
        <Card className="rounded-md">
          <CardHeader>
            <CardTitle className="text-sm font-mono uppercase flex items-center gap-2">
              <Target className="h-4 w-4 text-amber-500" />
              Top 5 Clienti per Fatturato
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.clienti.topClienti.map((cliente, index) => (
                <div 
                  key={cliente.nome}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-md"
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      index === 0 ? 'bg-amber-500 text-black' : 
                      index === 1 ? 'bg-gray-400 text-black' : 
                      index === 2 ? 'bg-amber-700 text-white' : 
                      'bg-muted text-muted-foreground'
                    }`}>
                      {index + 1}
                    </span>
                    <span className="font-medium text-sm truncate max-w-[200px]">{cliente.nome}</span>
                  </div>
                  <span className="font-mono font-bold text-emerald-600">
                    {formatCurrency(cliente.fatturato)}
                  </span>
                </div>
              ))}
              {stats.clienti.topClienti.length === 0 && (
                <p className="text-center text-muted-foreground py-8">Nessun dato disponibile</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
